import React, { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { RDS, RootState, identifyStrategy, strategy } from 'ouisys-engine/strategy';
import { useSelector } from 'react-redux';
import { Disclaimers, LanguageSwitcher, TopLegals, defineMessages } from 'ouisys-component-library';
import FLOWS from './__doNotModify/includedFlows';
import COMPS from './__doNotModify/includedComponents';
import ProvidersWrapper from './providers/ProvidersWrapper';
import { FormattedMessage, legals } from './localization';
import { useRootContext } from './providers/RootContext';
import { tracker } from 'ouisys-engine/utilities/tracker';
import { useDispatch } from './__doNotModify/store';
import PreLander from './components/PreLander';
import Loader from './components/Loader';

// import './Bloxz.scss';
import './Root.scss';
import CheckBoxGR from './components/CheckBoxGR';

// use require for importing assets like images
const fileImage = require('./assets/imgs/file.png');

// Define error messages here
const defaultMessages = defineMessages({
  InvalidMSISDN: {
    id: 'InvalidMSISDN',
    defaultMessage: 'This mobile number is incorrect, try again'
  },
  AlreadySubscribed: {
    id: 'AlreadySubscribed',
    defaultMessage: 'You are already a subscriber!'
  },
  UnknownError: {
    id: 'UnknownError',
    defaultMessage: 'An unknown error occurred.'
  },
  InvalidPIN: {
    id: 'InvalidPIN',
    defaultMessage: 'Please enter the correct PIN that you received on your phone'
  },
  SessionBlocked: {
    id: 'SessionBlocked',
    defaultMessage: 'Session Blocked!'
  },
  TooEarly: {
    id: 'TooEarly',
    defaultMessage: 'You must first submit a mobile number'
  },
  didnt_receive_pin_yet: {
    id: 'didnt_receive_pin_yet',
    defaultMessage: "Didn't receive PIN yet?"
  }
});

const Root = () => {
  const fileName = 'DWtJ-0LPq-evGA-OjbP-ZM9o';

  const { currentState } = useSelector((state: RootState) => state.strategy);
  const {
    locale,
    switchLang,
    isMoview,
    isChecked,
    defaultFlow,
    skipPrelander,
    setChecked,
    setPrelander,
    phoneInputRef,
    setShake
  } = useRootContext();
  const [showOverlay, setShowOverlay] = useState(false);

  const overlayRef = useRef(null);

  const dispatch = useDispatch();

  // when user clicks on the creative area, we show an overlay
  // so the user will focus on the subscription form
  // Function to handle the div focus, now optimized with useRef and useState
  const divFocus = useCallback(() => {
    tracker.customEvent('creativeArea', 'click', 'form-focus');
    setShowOverlay(true);
    setShake(true);

    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
    // Trigger a vibration if available
    if ('vibrate' in navigator) {
      navigator.vibrate([100, 30, 100, 30, 100]);
    }

    // Reset shake effect after 1 second (or the duration of your shake animation)
    setTimeout(() => setShake(false), 1000);
  }, []);

  const identifyStrategyEffect = useCallback(() => {
    dispatch(identifyStrategy());
  }, [dispatch]);

  useEffect(() => {
    identifyStrategyEffect();
  }, [identifyStrategyEffect]);

  return (
    <div className={`root ${process.env.country}`}>
      {/* pre-lander */}
      {!skipPrelander && (
        <PreLander
          contentLoaded={() => {
            tracker.advancedInPreFlow('advance-auto', 'step1');
            setPrelander(true);
          }}
        />
      )}

      <div className={`container ${locale === 'ar' ? 'direction-rtl' : ''} display-${skipPrelander} fadeIn`}>
        <div id="overLay" className={`overlay ${showOverlay ? 'show' : 'hide'}`} ref={overlayRef} />
        {/* <LanguageSwitcher
          locale={locale}
          switchLang={switchLang}
          options={[
            { value: 'en', label: 'English' },
            { value: 'ar', label: 'عربى' }
          ]}
          className="switchLang"
          tracker={tracker}
        /> */}
        <div className="topLegalsWrap">
          <TopLegals className="topLegals" locale={locale} legals={legals} flow={defaultFlow} />
        </div>

        <div className="creativeArea" onClick={divFocus}>
          <div className="file">
            <img alt="fileimage" className="file_image" src={fileImage} alt="File" />
            <div className="file_details">
              <p className="file_details_message">
                <FormattedMessage id="creative_message" defaultMessage="File is ready to download" />
              </p>
              <p className="file_details_name">
                <FormattedMessage id="creative_name" defaultMessage="Name:" /> <i>{fileName}</i>
              </p>
              <p className="file_details_expiration">
                <FormattedMessage id="creative_expiration" defaultMessage="Expiration:" /> 5{' '}
                <FormattedMessage id="creative_minutes" defaultMessage="minutes" />
              </p>
            </div>
          </div>
        </div>

        <div className="subscriptionAreaWrapper" onClick={(ev) => ev.stopPropagation()}>
          {strategy({
            identifyInitialState: () => <Loader />,
            // identifyFlowByMsisdn: (rds) => <COMPS.IdentifyFlowByMsisdn rds={rds} />,
            identifyFlowByOperatorSelect: () => <COMPS.OperatorSelection />,
            pinFlow: () => <FLOWS.PinFlow />,
            moFlow: () => {
              return <FLOWS.MoFlow />;
            },
            moRedirFlow: () => <FLOWS.MoRedirFlow />,
            oneClickFlow: () => <FLOWS.OneClickFlow />,
            click2smsFlow: () => <FLOWS.Click2smsFlow />,
            ussdFlow: () => <FLOWS.UssdFlow />,
            creditCardFlow: () => <FLOWS.CreditCardFlow />
          })(currentState)}
        </div>
        {RDS.WhenLoading(null, () => (
          <div className="loading-wrapper">
            <Loader />
            <div>
              <FormattedMessage id="loadingLabel" defaultMessage="Please wait..." />
            </div>
          </div>
        ))(currentState as any)}

        <div className="legalArea">
          {/* greece specific */}
          {!isMoview && (
            <CheckBoxGR
              checked={isChecked}
              onCheckedChange={(checked) => setChecked(checked)}
              onClicked={() => tracker.advancedInPreFlow('click-checkbox', 'agree')}
            />
          )}
          <Disclaimers
            className={`disclaimer brand-${process.env.service} ${process.env.country}`}
            locale={locale}
            legals={legals}
            flow={defaultFlow}
          />
        </div>
      </div>
    </div>
  );
};

const R = () => (
  <ProvidersWrapper>
    <Root />
  </ProvidersWrapper>
);

export default R;
